// Enhanced Milestone Configuration System - Excel Specification Compliant
// This builds upon the existing milestoneConfig.ts with full Excel coverage

import React from 'react';
import { milestoneCellColorConfig } from './milestoneCellColorConfig';
import { getExcelHoverData } from './config/excelLookupData';
import {
  type EnhancedMilestoneStatus,
  ENHANCED_MILESTONE_STATUSES,
  ENHANCED_STATUS_MENUS,
  getMilestoneStatusLabel
} from './config/statusDefinitions';

// Status definitions and menus imported from config/statusDefinitions.ts
// Re-export for backward compatibility
export {
  type EnhancedMilestoneStatus,
  ENHANCED_MILESTONE_STATUSES,
  ENHANCED_STATUS_MENUS,
  getMilestoneStatusLabel
};

// Enhanced utility functions
export const getEnhancedStatusOptions = (
  reviewType: string,
  approvalRequired?: boolean
): string[] => {
  // Determine the key based on approval condition and review type
  let menuKey = reviewType;

  if (approvalRequired === false) {
    // NO Approval Required
    if (reviewType === 'Meeting') {
      menuKey = 'No Approval Required Meeting';
    } else if (reviewType === 'Async') {
      menuKey = 'No Approval Required Async';
    }
  } else if (approvalRequired === true) {
    // Approval Required - use the base reviewType
    menuKey = reviewType;
  }
  // If approvalRequired is undefined, fallback to existing behavior (use reviewType as-is)

  return ENHANCED_STATUS_MENUS[menuKey] || [];
};

/** Mirrors timeline-config.yml doc-link-policy values (Feature 11 / CSME-110308). */
export type DocLinkPolicy =
  | 'NEVER'
  | 'REQUIRED_ON_APPROVED'
  | 'REQUIRED_ON_APPROVED_OR_COMPLETE';

/**
 * Per-milestone-name field overrides, keyed by UPPERCASE milestone name.
 *
 * `approvalRequired` — overrides the API-supplied milestoneAttributes.approvalRequired
 *   value. Set to true/false to reflect the product matrix; leave absent to fall back
 *   to the API value.
 *
 * `docLinkPolicy` — when set, overrides statusDefinitions blanket document_link rules
 *   to match backend MilestoneDocLinkValidator / timeline-config.yml.
 *
 * `alwaysHide` — field IDs forced into hidden regardless of reviewType/status/tier.
 */
const MILESTONE_NAME_OVERRIDES: Record<
  string,
  {
    approvalRequired?: boolean;
    docLinkPolicy?: DocLinkPolicy;
    alwaysHide?: string[];
    excludeReviewTypes?: string[];
  }
> = {
  MVP: {
    approvalRequired: true,
    docLinkPolicy: 'REQUIRED_ON_APPROVED',
    alwaysHide: ['document_link']
  },
  GTMS: { approvalRequired: true, docLinkPolicy: 'REQUIRED_ON_APPROVED' },
  E2E: { approvalRequired: true, docLinkPolicy: 'REQUIRED_ON_APPROVED' },
  E2E1: { approvalRequired: true, docLinkPolicy: 'REQUIRED_ON_APPROVED' },
  BRND: { approvalRequired: true, docLinkPolicy: 'REQUIRED_ON_APPROVED' },
  MONTZN: { approvalRequired: true, docLinkPolicy: 'REQUIRED_ON_APPROVED' },
  'MEDIA BRAINSTORM': {
    approvalRequired: false,
    docLinkPolicy: 'NEVER',
    excludeReviewTypes: ['Async']
  },
  GTMP: { approvalRequired: true, docLinkPolicy: 'REQUIRED_ON_APPROVED' },
  'CREATIVE ARCH': {
    approvalRequired: true,
    docLinkPolicy: 'REQUIRED_ON_APPROVED'
  },
  E2E2: { approvalRequired: true, docLinkPolicy: 'REQUIRED_ON_APPROVED' },
  MEDIA: { approvalRequired: true, docLinkPolicy: 'REQUIRED_ON_APPROVED' },
  RR1: {
    approvalRequired: false,
    docLinkPolicy: 'REQUIRED_ON_APPROVED_OR_COMPLETE'
  },
  RR2: {
    approvalRequired: false,
    docLinkPolicy: 'REQUIRED_ON_APPROVED_OR_COMPLETE'
  },
  'MKTG PKG': {
    approvalRequired: false,
    docLinkPolicy: 'REQUIRED_ON_APPROVED_OR_COMPLETE'
  }
};

const isApprovedStatus = (status: string): boolean =>
  status.toLowerCase().includes('approved');

const isCompleteStatus = (status: string): boolean =>
  status.toLowerCase().includes('complete');

/** Same rules as backend MilestoneDocLinkValidator.isPolicyTriggered(). */
export const isDocumentLinkRequiredForPolicy = (
  policy: DocLinkPolicy | undefined,
  status: string
): boolean => {
  if (!policy || policy === 'NEVER' || !status) return false;
  if (policy === 'REQUIRED_ON_APPROVED') return isApprovedStatus(status);
  if (policy === 'REQUIRED_ON_APPROVED_OR_COMPLETE') {
    return isApprovedStatus(status) || isCompleteStatus(status);
  }
  return false;
};

export function getMilestoneDocLinkPolicy(
  milestoneName?: string
): DocLinkPolicy | undefined {
  if (!milestoneName) return undefined;
  return MILESTONE_NAME_OVERRIDES[milestoneName.toUpperCase().trim()]
    ?.docLinkPolicy;
}

const demoteDocumentLinkFromRequired = (
  finalRequired: string[],
  finalOptional: string[],
  finalHidden: string[]
): void => {
  const docLinkIdx = finalRequired.indexOf('document_link');
  if (docLinkIdx === -1) return;
  finalRequired.splice(docLinkIdx, 1);
  if (
    !finalOptional.includes('document_link') &&
    !finalHidden.includes('document_link')
  ) {
    finalOptional.push('document_link');
  }
};

const promoteDocumentLinkToRequired = (
  finalRequired: string[],
  finalOptional: string[],
  finalHidden: string[]
): void => {
  if (finalHidden.includes('document_link')) return;
  const optIdx = finalOptional.indexOf('document_link');
  if (optIdx > -1) finalOptional.splice(optIdx, 1);
  if (!finalRequired.includes('document_link')) {
    finalRequired.push('document_link');
  }
};

/**
 * Returns the config-driven `approvalRequired` for a milestone name, or `undefined`
 * if the milestone is not in the matrix config. Call sites can use this to override
 * the API-supplied `milestoneAttributes.approvalRequired`.
 */
export function getMilestoneApprovalRequired(
  milestoneName?: string
): boolean | undefined {
  if (!milestoneName) return undefined;
  return MILESTONE_NAME_OVERRIDES[milestoneName.toUpperCase().trim()]
    ?.approvalRequired;
}

export function getMilestoneExcludedReviewTypes(
  milestoneName?: string
): string[] {
  if (!milestoneName) return [];
  return (
    MILESTONE_NAME_OVERRIDES[milestoneName.toUpperCase().trim()]
      ?.excludeReviewTypes ?? []
  );
}

export const getEnhancedEditFields = (
  reviewType: string,
  status: string,
  existingData?: any,
  tier?: string,
  approvalRequired?: boolean,
  milestoneName?: string
): {
  required: string[];
  optional: string[];
  hidden: string[];
  conditional: string[];
} => {
  // Config-driven approvalRequired takes precedence over the API-supplied value.
  const nameKey = milestoneName?.toUpperCase().trim();
  const nameOverride = nameKey ? MILESTONE_NAME_OVERRIDES[nameKey] : undefined;
  const resolvedApprovalRequired =
    nameOverride?.approvalRequired !== undefined
      ? nameOverride.approvalRequired
      : approvalRequired;

  // Safely convert status to uppercase - handle null, undefined, or non-string values
  const statusKey =
    status && typeof status === 'string' ? status.toUpperCase() : '';
  const statusConfig = ENHANCED_MILESTONE_STATUSES[statusKey];

  // Handle case when no status is selected yet but we have a review type
  if (!statusConfig) {
    // For Meeting and Async types, show status field so user can select it
    if (['Meeting', 'Async'].includes(reviewType)) {
      return {
        required: ['type_of_review', 'status'],
        optional: ['document_link', 'recording_link', 'notes_link'], // Always show link fields as optional by default
        hidden: [],
        conditional: []
      };
    }

    // For TBD and Not Needed, don't show status field
    return {
      required: ['type_of_review'],
      optional: ['document_link', 'recording_link', 'notes_link'], // Always show link fields as optional by default
      hidden: [],
      conditional: []
    };
  }

  const requirements = statusConfig.editFieldRequirements || {
    required: ['type_of_review'],
    optional: ['document_link', 'recording_link', 'notes_link'], // Always show link fields as optional by default
    hidden: [],
    conditional: {}
  };

  // Handle conditional fields
  const conditional: string[] = [];
  Object.entries(requirements.conditional).forEach(([field, conditions]) => {
    if (conditions.includes(status)) {
      conditional.push(field);
    }
  });

  // Ensure status field is always visible for Meeting/Async types, regardless of individual status config
  const finalRequired = [...requirements.required];
  const finalOptional = [...requirements.optional];
  const finalHidden = [...requirements.hidden];

  if (['Meeting', 'Async'].includes(reviewType)) {
    // Always show status field for Meeting/Async types
    if (
      !finalRequired.includes('status') &&
      !finalOptional.includes('status')
    ) {
      finalOptional.push('status');
    }
    // Remove status from hidden if it was there
    const statusHiddenIndex = finalHidden.indexOf('status');
    if (statusHiddenIndex > -1) {
      finalHidden.splice(statusHiddenIndex, 1);
    }

    // Special case: Meeting + Target should show date field (required - date is mandatory)
    if (reviewType === 'Meeting' && statusKey === 'TARGET') {
      if (!finalRequired.includes('date')) {
        finalRequired.push('date');
        // Remove from optional if it was there
        const dateOptionalIndex = finalOptional.indexOf('date');
        if (dateOptionalIndex > -1) {
          finalOptional.splice(dateOptionalIndex, 1);
        }
      }
      // Remove date from hidden if it was there
      const dateHiddenIndex = finalHidden.indexOf('date');
      if (dateHiddenIndex > -1) {
        finalHidden.splice(dateHiddenIndex, 1);
      }
    }

    // Special case: Async + Target (ASYNC_TARGET) should show date field
    if (reviewType === 'Async' && statusKey === 'ASYNC_TARGET') {
      if (!finalRequired.includes('date') && !finalOptional.includes('date')) {
        finalRequired.push('date'); // Make it required for async target
      }
      // Remove date from hidden if it was there
      const dateHiddenIndex = finalHidden.indexOf('date');
      if (dateHiddenIndex > -1) {
        finalHidden.splice(dateHiddenIndex, 1);
      }
    }

    // L1/L2 Enhancement: Require approver when ANY status is set (first time) for Async or Meeting
    // Only for Approval Required milestones (red 'A') - not for "No Approval Required" milestones
    const isL1OrL2 = tier === 'L1' || tier === 'L2';
    const hasApprovalRequired = resolvedApprovalRequired === true;
    if (isL1OrL2 && hasApprovalRequired) {
      if (!finalRequired.includes('approver')) {
        finalRequired.push('approver');
      }
      const approverHiddenIndex = finalHidden.indexOf('approver');
      if (approverHiddenIndex > -1) {
        finalHidden.splice(approverHiddenIndex, 1);
      }
    } else if (!isL1OrL2) {
      // L3/other/TBD/unrecognized tier: the backend never sends default approvers here
      // (no fallback to L1/L2 lists), but the field should still be pickable — just not
      // mandatory. It's only enforced as required when the user opts into Send for
      // Approval (validated separately in EditMilestoneModal.computeValidationErrors).
      const approverHiddenIndex = finalHidden.indexOf('approver');
      if (approverHiddenIndex > -1) {
        finalHidden.splice(approverHiddenIndex, 1);
        if (
          !finalOptional.includes('approver') &&
          !finalRequired.includes('approver')
        ) {
          finalOptional.push('approver');
        }
      }
    }
  }

  // Apply per-milestone-name overrides (always wins over status/tier config)
  if (milestoneName) {
    const override =
      MILESTONE_NAME_OVERRIDES[milestoneName.toUpperCase().trim()];
    if (override?.alwaysHide) {
      for (const field of override.alwaysHide) {
        const reqIdx = finalRequired.indexOf(field);
        if (reqIdx > -1) finalRequired.splice(reqIdx, 1);
        const optIdx = finalOptional.indexOf(field);
        if (optIdx > -1) finalOptional.splice(optIdx, 1);
        if (!finalHidden.includes(field)) finalHidden.push(field);
      }
    }

    // Document link: statusDefinitions use blanket rules; policy narrows to wiki/BE matrix.
    if (override?.docLinkPolicy && status && typeof status === 'string') {
      if (isDocumentLinkRequiredForPolicy(override.docLinkPolicy, status)) {
        promoteDocumentLinkToRequired(
          finalRequired,
          finalOptional,
          finalHidden
        );
      } else {
        demoteDocumentLinkFromRequired(
          finalRequired,
          finalOptional,
          finalHidden
        );
      }
    }
  }

  return {
    required: finalRequired,
    optional: finalOptional,
    hidden: finalHidden,
    conditional
  };
};

export const validateEnhancedMilestoneData = (
  status: string,
  data: any
): { isValid: boolean; errors: string[] } => {
  // Safely convert status to uppercase for config lookup only
  const statusKey =
    status && typeof status === 'string' ? status.toUpperCase() : '';
  const statusConfig = ENHANCED_MILESTONE_STATUSES[statusKey];
  const errors: string[] = [];

  if (!statusConfig) {
    errors.push('Invalid status');
    return { isValid: false, errors };
  }

  const requirements = statusConfig.editFieldRequirements;
  if (requirements) {
    // Check required fields
    requirements.required.forEach(field => {
      if (!data[field] || data[field] === '') {
        errors.push(`${field} is required for ${statusConfig.label}`);
      }
    });

    // Apply custom validation rules
    if (statusConfig.validationRules) {
      Object.entries(statusConfig.validationRules).forEach(([field, rule]) => {
        if (data[field] && rule.customValidator) {
          if (!rule.customValidator(data[field], data)) {
            errors.push(`Invalid ${field} format`);
          }
        }
      });
    }
  }

  // Apply date relationship validation rules
  if (statusConfig.dateRelationshipRules) {
    Object.entries(statusConfig.dateRelationshipRules).forEach(
      ([field, rule]) => {
        const fieldValue = data[field];
        const compareFieldValue = data[rule.compareToField];

        if (fieldValue && compareFieldValue) {
          const dateValidationResult = validateDateRelationship(
            fieldValue,
            compareFieldValue,
            rule.relationshipType,
            rule.allowSameDate
          );

          if (!dateValidationResult.isValid) {
            errors.push(rule.errorMessage);
          }
        }
      }
    );
  }

  return { isValid: errors.length === 0, errors };
};

// Helper function to validate date relationships
export const validateDateRelationship = (
  date1: string | any,
  date2: string | any,
  relationshipType:
    | 'greater_than'
    | 'less_than'
    | 'greater_than_or_equal'
    | 'less_than_or_equal',
  allowSameDate: boolean = false
): { isValid: boolean; message?: string } => {
  try {
    // Helper function to check if a date is TBD or N/A
    const isSpecialDate = (date: any): boolean => {
      if (!date) return false;

      // Check for direct TBD or N/A string
      if (date === 'TBD' || date === 'N/A') return true;

      // Check for object with special type
      if (typeof date === 'object' && date.type === 'special') {
        return date.value === 'TBD' || date.value === 'N/A';
      }

      return false;
    };

    // Skip validation if either date is TBD or N/A
    // These are special dates that cannot be compared
    if (isSpecialDate(date1) || isSpecialDate(date2)) {
      return {
        isValid: true,
        message: 'Skipping validation for TBD/N/A dates'
      };
    }

    // Convert dates to comparable format
    const parsedDate1 = parseDateValue(date1);
    const parsedDate2 = parseDateValue(date2);

    if (!parsedDate1 || !parsedDate2) {
      return { isValid: false, message: 'Invalid date format' };
    }

    const date1Time = parsedDate1.getTime();
    const date2Time = parsedDate2.getTime();

    switch (relationshipType) {
      case 'greater_than':
        if (allowSameDate) {
          return { isValid: date1Time >= date2Time };
        }
        return { isValid: date1Time > date2Time };

      case 'less_than':
        if (allowSameDate) {
          return { isValid: date1Time <= date2Time };
        }
        return { isValid: date1Time < date2Time };

      case 'greater_than_or_equal':
        return { isValid: date1Time >= date2Time };

      case 'less_than_or_equal':
        return { isValid: date1Time <= date2Time };

      default:
        return { isValid: false, message: 'Invalid relationship type' };
    }
  } catch (error) {
    return { isValid: false, message: 'Error comparing dates' };
  }
};

// Helper function to parse various date formats
const parseDateValue = (dateValue: any): Date | null => {
  if (!dateValue) return null;

  try {
    // Handle JSON-like inputs from calendar (months/quarters)
    if (typeof dateValue === 'object') {
      if (
        dateValue.type === 'months' &&
        Array.isArray(dateValue.months) &&
        dateValue.months.length > 0
      ) {
        dateValue = dateValue.months[0];
      } else if (
        dateValue.type === 'quarters' &&
        Array.isArray(dateValue.quarters) &&
        dateValue.quarters.length > 0
      ) {
        dateValue = dateValue.quarters[0];
      }
    }

    // Handle JSON string inputs
    if (
      typeof dateValue === 'string' &&
      (dateValue.startsWith('{') || dateValue.startsWith('['))
    ) {
      try {
        const parsed = JSON.parse(dateValue);
        if (
          parsed.type === 'months' &&
          Array.isArray(parsed.months) &&
          parsed.months.length > 0
        ) {
          dateValue = parsed.months[0];
        } else if (
          parsed.type === 'quarters' &&
          Array.isArray(parsed.quarters) &&
          parsed.quarters.length > 0
        ) {
          dateValue = parsed.quarters[0];
        }
      } catch (e) {
        // If JSON.parse fails, continue with original value
      }
    }

    // Handle quarter format (Q1 2025, Q2 2025, etc.)
    if (typeof dateValue === 'string' && dateValue.match(/^Q[1-4]\s+\d{4}$/)) {
      const [quarter, year] = dateValue.split(' ');
      const quarterNum = parseInt(quarter.replace('Q', ''));
      const quarterStartMonths = { 1: 0, 2: 3, 3: 6, 4: 9 }; // 0-indexed months
      return new Date(parseInt(year), quarterStartMonths[quarterNum], 1);
    }

    // Handle month-year format (Jan 2025, January 2025, etc.)
    if (
      typeof dateValue === 'string' &&
      dateValue.match(/^[A-Za-z]+\s+\d{4}$/)
    ) {
      const [monthStr, yearStr] = dateValue.split(' ');
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
      ];
      const monthNamesLong = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
      ];

      let monthIndex = monthNames.findIndex(
        m => m.toLowerCase() === monthStr.toLowerCase()
      );
      if (monthIndex === -1) {
        monthIndex = monthNamesLong.findIndex(
          m => m.toLowerCase() === monthStr.toLowerCase()
        );
      }

      if (monthIndex !== -1) {
        return new Date(parseInt(yearStr), monthIndex, 1);
      }
    }

    // Handle standard date formats
    const date = new Date(dateValue);
    return isNaN(date.getTime()) ? null : date;
  } catch (error) {
    return null;
  }
};

// Helper function to get icon from color config or enhanced config
export const getEnhancedMilestoneIcon = (
  status: string,
  data: any
): string | undefined => {
  const statusConfig = ENHANCED_MILESTONE_STATUSES[status?.toUpperCase()];

  if (!statusConfig) return undefined;

  // Try to get icon from color config first
  if (data && data.approvalCondition && data.reviewType) {
    const colorConfigKey = `${data.approvalCondition}|${data.reviewType}|${status}`;
    const colorConfig = milestoneCellColorConfig[colorConfigKey];

    if (colorConfig) {
      // If icon is explicitly set to null, return undefined to hide the icon
      if (colorConfig.icon === null) {
        return undefined;
      }
      // If icon is set to a string, return it
      if (colorConfig.icon) {
        return colorConfig.icon;
      }
    }
  }

  // Fallback to enhanced config icon
  return statusConfig.icon;
};

// Enhanced cell display formatting that matches Excel exactly
export const formatEnhancedCellDisplay = (
  status: string,
  data: any
): string => {
  // Safely convert status to string first, then uppercase for config lookup only (not for display)
  // Handle cases where status might be a number (e.g., HTTP status codes like 200)
  if (!status) return '';
  const statusStr = typeof status === 'string' ? status : String(status);
  // Skip if status looks like a number (e.g., "200") - this shouldn't be a milestone status
  if (/^\d+$/.test(statusStr.trim())) {
    return ''; // Return empty if status is just a number
  }
  const statusKey = statusStr.toUpperCase();
  const statusConfig = ENHANCED_MILESTONE_STATUSES[statusKey];

  if (!statusConfig) return '';

  let displayParts: string[] = [];

  // Get additionalText from color config if available
  const colorConfigKey = `${data.approvalCondition}|${data.reviewType}|${status}`;
  const colorConfig = milestoneCellColorConfig[colorConfigKey];

  // Use additionalText from color config or fallback to cellDisplayText
  const additionalText =
    colorConfig?.additionalText || statusConfig.cellDisplayText;

  // Check if we should show only secondary date
  const showOnlySecondaryDate =
    data.secondaryDate &&
    statusConfig.showSecondaryDate &&
    statusConfig.cellSecondaryDateFormat;

  // Determine if this is the SENT status (exception case where text comes before date)
  const isSentStatus = statusKey === 'SENT';

  // For SENT status: text first, then date (e.g., "Due on Oct 15")
  // For all other statuses: date first, then text (e.g., "Oct 15 Follow-up on")

  if (isSentStatus && additionalText) {
    displayParts.push(additionalText);
  }

  // Add formatted primary date - SKIP if showing only secondary date
  if (
    !showOnlySecondaryDate &&
    data.date &&
    statusConfig.cellDateFormat &&
    statusConfig.cellDateFormat !== 'n/a'
  ) {
    const formattedDate = formatDateString(
      data.date,
      statusConfig.cellDateFormat
    );
    displayParts.push(formattedDate);
  }

  // Add secondary date for specific statuses - only if cellSecondaryDateFormat is explicitly defined
  // This prevents secondary dates from showing in cells when we only want them in popups
  if (showOnlySecondaryDate && statusConfig.cellSecondaryDateFormat) {
    const formattedSecondaryDate = formatDateString(
      data.secondaryDate,
      statusConfig.cellSecondaryDateFormat
    );
    displayParts.push(formattedSecondaryDate);
  }

  // For non-SENT statuses: add text after date
  if (!isSentStatus && additionalText) {
    displayParts.push(additionalText);
  }

  // Add formatted time - now on separate line without "at"
  if (
    data.meetingScheduledAt &&
    statusConfig.cellTimeFormat &&
    statusConfig.cellTimeFormat !== 'n/a'
  ) {
    // Just use the time string directly without any "at" prefix
    // Time is already in 24-hour format from convertMilestoneTimestampToLocal
    displayParts.push(data.meetingScheduledAt);
  }

  // Always join with newlines to show date and time on separate lines
  return displayParts.length > 0 ? displayParts.join('\n') : '';
};

/**
 * Get the date to use for "past date" red styling in milestone cells.
 * When the cell displays the secondary date (due date, pending date), use that for the check.
 * Otherwise use the primary date.
 */
export function getDateForPastCheck(
  milestone: { date?: string | null; secondaryDate?: string | null },
  statusKey: string
): string | null {
  const statusConfig = ENHANCED_MILESTONE_STATUSES[statusKey];
  if (!statusConfig) return milestone.date || null;

  const showOnlySecondaryDate =
    !!milestone.secondaryDate &&
    !!statusConfig.showSecondaryDate &&
    !!statusConfig.cellSecondaryDateFormat;

  if (showOnlySecondaryDate) {
    return milestone.secondaryDate || null;
  }
  return milestone.date || null;
}

// Enhanced cell styling function
export function getEnhancedCellStyling(
  statusKey: string,
  data?: any
): React.CSSProperties {
  const statusConfig = ENHANCED_MILESTONE_STATUSES[statusKey];

  if (!statusConfig) {
    return {
      backgroundColor: '#FFFFFF',
      color: '#222222'
    };
  }

  // Try to get styling from color config first if data is available
  let cellColor = statusConfig.cellColor;
  let textColor = statusConfig.textColor || '#222222';

  if (data && data.approvalCondition && data.reviewType) {
    const colorConfigKey = `${data.approvalCondition}|${data.reviewType}|${statusKey}`;
    const colorConfig = milestoneCellColorConfig[colorConfigKey];

    if (colorConfig) {
      cellColor = colorConfig.cellColor;
      textColor = colorConfig.textColor;
    }
  }

  const baseStyle: React.CSSProperties = {
    backgroundColor: cellColor,
    color: textColor
  };

  // Special striped pattern for not applicable, not required, or optional milestones - Exact Figma specs
  if (statusConfig.striped) {
    return {
      ...baseStyle,
      background: `
        repeating-linear-gradient(
          135deg,
          ${cellColor} 0px,
          ${cellColor} 4px,
          #E2EDE0 4px,
          #E2EDE0 6px
        )
      `,
      color: '#666666'
    };
  }

  return baseStyle;
}

// Helper function to get dynamic field label based on enhanced status configuration
export const getEnhancedDynamicFieldLabel = (
  fieldId: string,
  status: string,
  existingData?: any
): string => {
  // Safely convert status to uppercase for config lookup only
  const statusKey =
    status && typeof status === 'string' ? status.toUpperCase() : '';
  const statusConfig = ENHANCED_MILESTONE_STATUSES[statusKey];

  if (fieldId === 'approver' && statusConfig?.historicalFields) {
    if (
      existingData?.approver &&
      statusConfig.historicalFields.showHistoricalData
    ) {
      return statusConfig.historicalFields.approverLabel || 'APPROVER';
    }
  }

  if (
    fieldId === 'historical_approval_date' &&
    statusConfig?.historicalFields
  ) {
    return statusConfig.historicalFields.dateLabel || 'APPROVED ON';
  }

  // Handle dynamic secondary date labels based on enhanced status configuration
  if (fieldId === 'secondary_date' && statusConfig?.secondaryDateLabel) {
    return statusConfig.secondaryDateLabel.toUpperCase();
  }

  // Fallback to default labels
  if (fieldId === 'secondary_date') {
    return 'Secondary Date';
  }

  // Return default label - no need to import MILESTONE_FORM_FIELDS here
  return fieldId.toUpperCase().replace('_', ' ');
};

// Enhanced popup text formatting - Updated to include L+M+N+O+P+Q concatenation
export const formatEnhancedPopupText = (
  status: string,
  approvalRequired?: boolean,
  reviewType?: string,
  date?: string,
  time?: string,
  secondaryDate?: string,
  approver?: string
): string => {
  // Create combination key for Excel lookup
  const approvalCondition = approvalRequired
    ? 'APPROVAL_REQUIRED'
    : 'NO_APPROVAL_REQUIRED';
  const typeOfReview = reviewType?.toUpperCase() || 'TBD';

  // Clean status mapping - remove review type prefix/suffix if it exists
  // Safely convert status to uppercase for lookup only
  let statusMenu =
    status && typeof status === 'string' ? status.toUpperCase() : '';

  // Remove review type prefixes/suffixes from status for all status types
  if (typeOfReview === 'MEETING') {
    statusMenu = statusMenu.replace(/^MEETING_/, '');
  } else if (typeOfReview === 'ASYNC') {
    statusMenu = statusMenu.replace(/^ASYNC_/, '').replace(/_ASYNC$/, '');
  }

  // Special mapping for SENT_PENDING to match Excel lookup keys
  if (statusMenu === 'SENT_PENDING') {
    statusMenu = 'SENT_FEEDBACK_PENDING';
  }

  // Special mapping for COMPLETE to match Excel lookup keys (COMPLETED)
  if (statusMenu === 'COMPLETE') {
    statusMenu = 'COMPLETED';
  }

  // Special mapping for REPLANNING_NO_APPROVAL to match Excel lookup keys
  // Both REPLANNING_NO_APPROVAL and REPLANNING_APPROVAL_REQUIRED use the same Excel key
  if (statusMenu === 'REPLANNING_NO_APPROVAL' || statusMenu === 'REPLANNING') {
    statusMenu = 'REPLANNING_APPROVAL_REQUIRED';
  }

  // Map to Excel data based on the combination
  const hoverData = getExcelHoverData(
    approvalCondition,
    typeOfReview,
    statusMenu
  );

  if (!hoverData) {
    // Fallback to original implementation
    const statusKey =
      status && typeof status === 'string' ? status.toUpperCase() : '';
    const statusConfig = ENHANCED_MILESTONE_STATUSES[statusKey];
    if (!statusConfig) return '';
    return statusConfig.rolloverCopy;
  }

  let displayText = '';

  // Get L+M+N concatenation (hover fields)
  let hoverText = hoverData.hoverText || '';

  // Smart handling of [Mon] or [Mon XX] based on date precision
  // This MUST run FIRST to avoid the generic [Mon XX] replacement from interfering
  if (date && hoverText.includes('[Mon] or [Mon XX]')) {
    let formattedDate = '';

    // Check date precision to choose appropriate format
    if (date.match(/^Q[1-4]\s+\d{4}$/)) {
      // Quarter format (e.g., "Q1 2025") - show just quarter (e.g., "Q1")
      const [quarter] = date.split(' ');
      formattedDate = quarter;
    } else if (date.match(/^[A-Za-z]+\s+\d{4}$/)) {
      // Month-year format (e.g., "APR 2025", "January 2025") - show just month
      const [monthStr] = date.split(' ');
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
      ];
      const monthNamesLong = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
      ];

      const monthIndex = monthNames.findIndex(
        m => m.toLowerCase() === monthStr.toLowerCase()
      );
      const monthIndexLong = monthNamesLong.findIndex(
        m => m.toLowerCase() === monthStr.toLowerCase()
      );

      if (monthIndex !== -1) {
        formattedDate = monthNames[monthIndex];
      } else if (monthIndexLong !== -1) {
        formattedDate = monthNames[monthIndexLong];
      } else {
        formattedDate = monthStr; // Fallback to original
      }
    } else {
      // Specific date (e.g., "2025-04-15", "Apr 15") - show month and day
      formattedDate = formatDateString(date, '[Mon XX]');
    }

    // Replace the placeholder with the formatted date
    hoverText = hoverText.replace(/\[Mon\] or \[Mon XX\]/g, formattedDate);
    // console.log('🔧 Smart date formatting applied:', {
    //   originalDate: date,
    //   chosenFormat: formattedDate,
    //   dateType: date.match(/^Q[1-4]\s+\d{4}$/)
    //     ? 'quarter'
    //     : date.match(/^[A-Za-z]+\s+\d{4}$/)
    //     ? 'month-year'
    //     : 'specific-date'
    // });
  }

  // Replace remaining date placeholders with actual dates (after [Mon] or [Mon XX] handling)
  if (
    date &&
    (hoverText.includes('[Mon XX]') || hoverText.includes('[Mon XX, YYYY]'))
  ) {
    const formattedShort = formatDateString(date, '[Mon XX]');
    const formattedLong = formatDateString(date, '[Mon XX, YYYY]');
    hoverText = hoverText
      .replace(/\[Mon XX, YYYY\]/g, formattedLong)
      .replace(/\[Mon XX\]/g, formattedShort);
  }

  // Also handle standalone [Mon] patterns that might remain
  if (
    date &&
    hoverText.includes('[Mon]') &&
    !hoverText.includes('[Mon] or [Mon XX]')
  ) {
    const formattedDate = formatDateString(date, '[Mon]');
    hoverText = hoverText.replace(/\[Mon\]/g, formattedDate);
  }

  // Replace time placeholders with actual times (and remove "at" prefix)
  if (time) {
    if (hoverText.includes('[11:11AM - 12:12PM PST]')) {
      hoverText = hoverText.replace(/at \[11:11AM - 12:12PM PST\]/g, time);
      hoverText = hoverText.replace(/\[11:11AM - 12:12PM PST\]/g, time);
    }
    if (hoverText.includes('[11:11AM - 12:12PM UTC]')) {
      hoverText = hoverText.replace(/at \[11:11AM - 12:12PM UTC\]/g, time);
      hoverText = hoverText.replace(/\[11:11AM - 12:12PM UTC\]/g, time);
    }
  }

  // Remove 'n/a' if it appears at the end
  hoverText = hoverText.replace(/\s+n\/a$/, '');

  displayText = hoverText;

  // Get O+P+Q concatenation (edit fields) and add to hover
  let editText = hoverData.editText || '';

  if (editText && editText !== 'n/a' && editText.trim() !== '') {
    // Replace date placeholders in edit text
    if (
      secondaryDate &&
      (editText.includes('[Mon XX]') || editText.includes('[Mon XX, YYYY]'))
    ) {
      const formattedSecondaryShort = formatDateString(
        secondaryDate,
        '[Mon XX]'
      );
      const formattedSecondaryLong = formatDateString(
        secondaryDate,
        '[Mon XX, YYYY]'
      );
      editText = editText
        .replace(/\[Mon XX, YYYY\]/g, formattedSecondaryLong)
        .replace(/\[Mon XX\]/g, formattedSecondaryShort);
    }

    // Remove 'n/a' from edit text
    editText = editText.replace(/\s+n\/a$/, '');

    if (editText.trim() !== '') {
      // Join on new line to match Figma layout (each statement on its own line)
      displayText += (displayText ? '\n' : '') + editText;
    }
  }

  return displayText;
};

// Excel data mapping - now imported from separate module
// const getExcelHoverData moved to config/excelLookupData.ts

// Helper function for date formatting
const formatDateString = (dateValue: string, format: string): string => {
  if (!dateValue) return '';

  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ];

  const monthNamesLong = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  // Handle different date formats
  let month = '';
  let day = '';
  let year = '';

  // Normalize JSON-like inputs from calendar (months/quarters)
  // Examples:
  // {"type": "months", "months": ["Oct 2025"]}
  // {"type": "quarters", "quarters": ["Q1 2025"]}
  // Handle when value is an OBJECT
  try {
    const anyVal: any = dateValue as any;
    if (anyVal && typeof anyVal === 'object') {
      if (
        anyVal.type === 'months' &&
        Array.isArray(anyVal.months) &&
        anyVal.months.length > 0
      ) {
        dateValue = String(anyVal.months[0]);
      } else if (
        anyVal.type === 'quarters' &&
        Array.isArray(anyVal.quarters) &&
        anyVal.quarters.length > 0
      ) {
        dateValue = String(anyVal.quarters[0]);
      }
    }
  } catch (_) {
    // ignore
  }

  if (typeof dateValue === 'string') {
    const trimmed = dateValue.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const parsed: any = JSON.parse(trimmed);
        if (
          parsed &&
          parsed.type === 'months' &&
          Array.isArray(parsed.months) &&
          parsed.months.length > 0
        ) {
          // Use the first selected month for display
          dateValue = String(parsed.months[0]);
        } else if (
          parsed &&
          parsed.type === 'quarters' &&
          Array.isArray(parsed.quarters) &&
          parsed.quarters.length > 0
        ) {
          // Use the first selected quarter for display
          dateValue = String(parsed.quarters[0]);
        }
      } catch (e) {
        // If JSON.parse fails, keep original value
      }
    }
  }

  // Preserve quarter or month-year inputs (show just quarter for cell display, similar to months)
  if (typeof dateValue === 'string' && dateValue.match(/^Q[1-4]\s+\d{4}$/)) {
    // For cell display, show just the quarter (e.g., "Q4" instead of "Q4 2025")
    const [quarter] = dateValue.split(' ');
    return quarter; // e.g., "Q4"
  }
  if (typeof dateValue === 'string' && dateValue.match(/^[A-Za-z]+\s+\d{4}$/)) {
    const [monthStr, yearStr] = dateValue.split(' ');
    const idxShort = monthNames.findIndex(
      m => m.toLowerCase() === monthStr.toLowerCase()
    );
    const idxLong = monthNamesLong.findIndex(
      m => m.toLowerCase() === monthStr.toLowerCase()
    );

    // For [Mon] or [Mon XX] format, return abbreviated month name only
    if (format === '[Mon] or [Mon XX]' || format === '[Mon XX]') {
      if (idxShort !== -1) {
        return monthNames[idxShort]; // Use abbreviated month (e.g., "Oct", "Dec")
      } else if (idxLong !== -1) {
        return monthNames[idxLong]; // Convert long to abbreviated (e.g., "October" -> "Oct")
      } else {
        return monthStr; // Fallback to original
      }
    }

    // For other formats, normalize to abbreviated month
    const monthAbbrev =
      idxShort !== -1
        ? monthNames[idxShort]
        : idxLong !== -1
        ? monthNames[idxLong]
        : monthStr;
    return `${monthAbbrev} ${yearStr}`;
  }

  // Handle Q1/Q2/Q3/Q4 format (e.g., "Q1 2025")
  if (dateValue.match(/^Q[1-4]\s+\d{4}$/)) {
    const [quarter, yearStr] = dateValue.split(' ');
    year = yearStr;
    const quarterNum = parseInt(quarter.replace('Q', ''));
    const quarterMonths = {
      1: ['Jan', 'Feb', 'Mar'],
      2: ['Apr', 'May', 'Jun'],
      3: ['Jul', 'Aug', 'Sep'],
      4: ['Oct', 'Nov', 'Dec']
    };
    month = quarterMonths[quarterNum][0]; // Use first month of quarter
    day = '1'; // Default to first day
  }
  // Handle "JAN 2025" or "January 2025" format
  else if (dateValue.match(/^[A-Za-z]+\s+\d{4}$/)) {
    const [monthStr, yearStr] = dateValue.split(' ');
    year = yearStr;
    // Find month index from either short or long name
    const monthIndex = monthNames.findIndex(
      m => m.toLowerCase() === monthStr.toLowerCase()
    );
    const monthIndexLong = monthNamesLong.findIndex(
      m => m.toLowerCase() === monthStr.toLowerCase()
    );

    if (monthIndex !== -1) {
      month = monthNames[monthIndex];
    } else if (monthIndexLong !== -1) {
      month = monthNames[monthIndexLong];
    } else {
      month = monthStr; // Fallback to original string
    }
    day = '1'; // Default to first day
  }
  // Handle standard date formats (2025-01-01, Jan 15, 2025, etc.)
  else {
    try {
      // IMPORTANT: Check if it's an ISO date string (YYYY-MM-DD) first
      // to avoid timezone conversion issues
      const isoDateMatch = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoDateMatch) {
        // Parse ISO date WITHOUT timezone conversion
        // Extract year, month, day directly from the string
        year = isoDateMatch[1];
        const monthNum = parseInt(isoDateMatch[2], 10);
        day = parseInt(isoDateMatch[3], 10).toString(); // Remove leading zero
        month = monthNames[monthNum - 1]; // monthNum is 1-indexed, array is 0-indexed
      } else {
        // For other formats, use Date parsing (this may have timezone issues)
        const date = new Date(dateValue);
        if (!isNaN(date.getTime())) {
          month = monthNames[date.getMonth()];
          day = date.getDate().toString();
          year = date.getFullYear().toString();
        } else {
          // If it's not a valid date, try to parse manually
          // Handle "Jan 15" format
          if (dateValue.match(/^[A-Za-z]+\s+\d{1,2}$/)) {
            const [monthStr, dayStr] = dateValue.split(' ');
            const monthIndex = monthNames.findIndex(
              m => m.toLowerCase() === monthStr.toLowerCase()
            );
            if (monthIndex !== -1) {
              month = monthNames[monthIndex];
              day = dayStr;
              year = new Date().getFullYear().toString(); // Use current year
            }
          }
          // Fallback: return original value if we can't parse it
          else {
            return dateValue;
          }
        }
      }
    } catch (e) {
      // If all parsing fails, return the original value
      return dateValue;
    }
  }

  // Apply the format template with smart handling of [Mon] or [Mon XX]
  if (format === '[Mon] or [Mon XX]') {
    // Smart format selection based on date precision
    if (dateValue.match(/^Q[1-4]\s+\d{4}$/)) {
      // Quarter format - return as-is
      return dateValue;
    } else if (dateValue.match(/^[A-Za-z]+\s+\d{4}$/)) {
      // Month-year format - return full month name only (not abbreviated)
      const [monthStr, yearStr] = dateValue.split(' ');
      const idxShort = monthNames.findIndex(
        m => m.toLowerCase() === monthStr.toLowerCase()
      );
      const idxLong = monthNamesLong.findIndex(
        m => m.toLowerCase() === monthStr.toLowerCase()
      );

      // Return full month name
      if (idxShort !== -1) {
        return monthNamesLong[idxShort]; // Convert short to long (e.g., "Oct" -> "October")
      } else if (idxLong !== -1) {
        return monthNamesLong[idxLong]; // Already long format
      } else {
        return monthStr; // Fallback to original
      }
    } else {
      // Specific date - return month and day (e.g., "Oct 15")
      return `${month} ${day}`;
    }
  }

  return format
    .replace('[Mon]', month)
    .replace('[Mon XX, YYYY]', `${month} ${day}, ${year}`)
    .replace('[Mon XX]', `${month} ${day}`);
};

// Export for backward compatibility and easy migration
export {
  ENHANCED_MILESTONE_STATUSES as MILESTONE_STATUSES,
  ENHANCED_STATUS_MENUS as STATUS_MENUS,
  getEnhancedStatusOptions as getStatusOptions,
  getEnhancedEditFields as getVisibleFields,
  validateEnhancedMilestoneData as validateMilestoneData,
  formatEnhancedCellDisplay as formatMilestoneDisplayText,
  getEnhancedCellStyling as getCellStyling,
  formatEnhancedPopupText as formatMilestonePopupText,
  getEnhancedMilestoneIcon as getMilestoneIcon,
  getEnhancedDynamicFieldLabel as getDynamicFieldLabel,
  getExcelHoverData
};

// Icon mapping for backward compatibility
export const MILESTONE_ICONS = {
  calendar: 'calendar',
  envelope: 'email',
  'arrow-circle': 'refresh',
  arrow_circle: 'refresh',
  clock: 'clock',
  checkmark: 'checkmark',
  close: 'close',
  lined: 'lined',
  none: 'none'
};

// Form field definitions for backward compatibility with EditMilestoneModal
export const MILESTONE_FORM_FIELDS = {
  TYPE_OF_REVIEW: {
    id: 'type_of_review',
    type: 'dropdown',
    label: 'TYPE OF REVIEW',
    required: true,
    options: ['TBD', 'Milestone Not Required', 'Meeting', 'Async']
  },
  STATUS: {
    id: 'status',
    type: 'dropdown',
    label: 'STATUS',
    required: true,
    options: []
  },
  DATE: {
    id: 'date',
    type: 'calendar',
    label: 'DATE',
    required: true
  },
  MEETING_SCHEDULED_ON: {
    id: 'meeting_scheduled_on',
    type: 'date',
    label: 'MEETING SCHEDULED ON',
    required: true
  },
  MEETING_SCHEDULED_AT: {
    id: 'meeting_scheduled_at',
    type: 'time',
    label: 'MEETING SCHEDULED AT',
    required: true
  },
  SECONDARY_DATE: {
    id: 'secondary_date',
    type: 'date',
    label: 'SECONDARY DATE',
    required: false,
    dynamicLabel: true
  },
  DOCUMENT_LINK: {
    id: 'document_link',
    type: 'url',
    label: 'DOCUMENT LINK',
    required: false
  },
  RECORDING_LINK: {
    id: 'recording_link',
    type: 'url',
    label: 'RECORDING LINK',
    required: false
  },
  NOTES_LINK: {
    id: 'notes_link',
    type: 'url',
    label: 'NOTES LINK',
    required: false
  },
  APPROVER: {
    id: 'approver',
    type: 'text',
    label: 'APPROVER',
    required: false,
    dynamicLabel: true
  }
} as const;
